import { useEffect, useState } from "react";
import { Card, Divider, Grid, Header, Icon, Label, Modal, Segment } from "semantic-ui-react";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { Permit } from "../models/Permit";
import { toastError, toastSuccess } from "../utils";
import ConfirmWithCommentsModal from "./common/ConfirmWithCommentsModal";
import ConfirmModal from "./common/ConfirmModal";
import ExtendPermitModal from "./ExtendPermitModal";
import CompletePermitModalContainer from "../containers/CompletePermitModalContainer";
import PermitAttachmentBar from "./PermitAttachmentBar";
import DocumentView from "./DocumentView";
import { Document_Data } from "../models/Document_Data";
import StateChangeLogsView from "./StateChangeLogsView";
import PermitDescriptionView from "./PermitDescriptionView";
import ViewItem from "./common/ViewItem";
import PermitAuthorizationView from "./PermitAuthorizationView";
import { SitePhoto } from "../models/SitePhoto";
import PhotoViewer from "./common/PhotoViewer";
import GangMembersView from "./GangMembersView";
import MapWithBoundaries from "./MapWithBoundaries";
import PermitMap from "./PermitMap";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import { MapItem } from "../utils/arcgisUtils";
import PermitPdf from "./permit-pdf/PermitPdf";
import { Organization } from "../models/Organization";
import ActionButton from "./common/DetailsBoxActionButton";
import LocationLogsView from "./LocationLogsView";
import PermitCalibrationCertificate from "./PermitCalibrationCertificate";
import PermitSitePhotos from "./PermitSitePhotos";
import { OrgUser } from "../models/OrgUser";
import { createProjectMapItem } from "../utils/boundaryUtils";
import { SystemDocumentType } from "../models/SystemDocumentType";

interface Props {
  showPermitBoundariesOnMap: boolean;
  downloadPdfButtonVisible?: boolean;
  rejectButtonVisible?: boolean;
  approveButtonVisible?: boolean;
  cancelButtonVisible?: boolean;
  undoCancellationButtonVisible?: boolean;
  extendButtonVisible?: boolean;
  completeButtonVisible?: boolean;
  editButtonVisible?: boolean;
  renewButtonVisible?: boolean;
  permit?: Permit;
  organization?: Organization;
  onClose: () => void;
  onEditPermit: () => void;
  onRenewPermit: () => void;
  onRejectPermit: (comments: string) => void;
  rejectPermitStatus: AsyncActionStatus;
  onApprovePermit: () => void;
  approvePermitStatus: AsyncActionStatus;
  onCancelPermit: () => void;
  cancelPermitStatus: AsyncActionStatus;
  onUndoCancelledPermit: () => void;
  undoCancelledPermitStatus: AsyncActionStatus;
  onExtendPermit: (to: Date, comments: string) => void;
  extendPermitStatus: AsyncActionStatus;
  onCompletePermit: () => void;
  completePermitStatus: AsyncActionStatus;
  onPermitRejected?: (permit: Permit) => void;
  onPermitApproved?: (permit: Permit) => void;
  onPermitCancelled?: (permit: Permit) => void;
  onPermitUndoCancellation?: (permit: Permit) => void;
  onPermitExtended?: (permit: Permit) => void;
  onPermitCompleted?: (permit: Permit) => void;
  onAsBuiltAdded?: () => void;
  addNewDocumentButtonVisible?: boolean;
  asBuiltCompletionDeclarations?: string[] | null;
  onRecreateEsriSnapshot?: () => void;
  reCreateEsriSnapshotVisible: boolean;
  recreateEsriSnapshotStatus: AsyncActionStatus;
  showAffectedServices?: boolean;
  showAsBuiltRequired?: boolean;
  showCalibrationCertificate?: boolean;
  gangMembersEnabled?: boolean;
  showSitePhotos?: boolean;
}

const PermitView = ({
  showPermitBoundariesOnMap,
  downloadPdfButtonVisible,
  rejectButtonVisible,
  approveButtonVisible,
  cancelButtonVisible,
  undoCancellationButtonVisible,
  extendButtonVisible,
  completeButtonVisible,
  editButtonVisible,
  renewButtonVisible,
  permit,
  organization,
  onClose,
  onEditPermit,
  onRenewPermit,
  onRejectPermit,
  rejectPermitStatus,
  onApprovePermit,
  approvePermitStatus,
  onCancelPermit,
  cancelPermitStatus,
  onUndoCancelledPermit,
  undoCancelledPermitStatus,
  onExtendPermit,
  extendPermitStatus,
  onCompletePermit,
  completePermitStatus,
  onPermitApproved,
  onPermitCompleted,
  onPermitExtended,
  onPermitRejected,
  onPermitCancelled,
  onPermitUndoCancellation,
  onAsBuiltAdded,
  addNewDocumentButtonVisible,
  asBuiltCompletionDeclarations,
  onRecreateEsriSnapshot,
  reCreateEsriSnapshotVisible,
  recreateEsriSnapshotStatus,
  showAffectedServices,
  showAsBuiltRequired,
  showCalibrationCertificate,
  gangMembersEnabled,
  showSitePhotos,
}: Props) => {
  const [showPdf, setShowPdf] = useState(false);
  const [openViewPermitModal, setOpenViewPermitModal] = useState(false);
  const [openConfirmRejectModal, setOpenConfirmRejectModal] = useState(false);
  const [openConfirmCancelModal, setOpenConfirmCancelModal] = useState(false);
  const [openConfirmUndoCancellationModal, setOpenConfirmUndoCancellationModal] = useState(false);
  const [openExtendModal, setOpenExtendModal] = useState(false);
  const [openCompleteModal, setOpenCompleteModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<Document_Data | undefined>(undefined);
  const [previewSitePhoto, setPreviewSitePhoto] = useState<SitePhoto | undefined>(undefined);
  const [previewBoundaries, setPreviewBoundaries] = useState(true);
  const [projectBoundaries, setProjectBoundaries] = useState<MapItem[]>([]);
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [showGangMembers, setShowGangMembers] = useState(false);

  // Display the Esri map or first SMD drawing by default
  useEffect(() => {
    if (showPermitBoundariesOnMap) {
      setPreviewBoundaries(true);
    } else {
      setPreviewBoundaries(false);

      const drawings = permit?.documents?.filter((d) => d.type.key.toLowerCase() === "drawing");
      if (drawings && drawings.length > 0) {
        setPreviewDoc(drawings[0]);
      } else {
        setPreviewDoc(undefined);
      }
    }

    setPreviewSitePhoto(undefined);
    setShowAuthorization(false);
    setShowGangMembers(false);
  }, [permit, showPermitBoundariesOnMap]);

  const handleViewPermitPopup = () => {
    setOpenViewPermitModal(true);
  };

  const handleModalClose = () => {
    setOpenViewPermitModal(false);
    setOpenConfirmRejectModal(false);
    setOpenConfirmCancelModal(false);
    setOpenConfirmUndoCancellationModal(false);
    setOpenExtendModal(false);
    setOpenCompleteModal(false);
    setPreviewDoc(undefined);
    setPreviewSitePhoto(undefined);

    rejectPermitStatus.reset();
    approvePermitStatus.reset();
    extendPermitStatus.reset();
    completePermitStatus.reset();
    cancelPermitStatus.reset();
    undoCancelledPermitStatus.reset();

    // not resetting here to ensure user cannot click again when switching between list and view
    // recreateEsriSnapshotStatus.reset();
  };

  useEffect(() => {
    if (rejectPermitStatus.successful) {
      toastSuccess("Permit rejected successfully!");
      setOpenConfirmRejectModal(false);
      if (onPermitRejected && permit) {
        onPermitRejected(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectPermitStatus.successful]);

  useEffect(() => {
    if (approvePermitStatus.successful) {
      toastSuccess("Permit approved successfully!");
      if (onPermitApproved && permit) {
        onPermitApproved(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvePermitStatus.successful]);

  useEffect(() => {
    if (cancelPermitStatus.successful) {
      toastSuccess("Permit cancelled successfully!");
      setOpenConfirmCancelModal(false);
      if (onPermitCancelled && permit) {
        onPermitCancelled(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelPermitStatus.successful]);

  useEffect(() => {
    if (undoCancelledPermitStatus.successful) {
      toastSuccess("Permit cancellation successfully revoked!");
      setOpenConfirmUndoCancellationModal(false);
      if (onPermitUndoCancellation && permit) {
        onPermitUndoCancellation(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoCancelledPermitStatus.successful]);

  useEffect(() => {
    if (extendPermitStatus.successful) {
      toastSuccess("Permit extended successfully!");
      setOpenExtendModal(false);
      if (onPermitExtended && permit) {
        onPermitExtended(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendPermitStatus.successful]);

  useEffect(() => {
    if (recreateEsriSnapshotStatus.successful) {
      toastSuccess("Esri snapshot created successfully!");
      recreateEsriSnapshotStatus.reset();
    } else if (recreateEsriSnapshotStatus.errors && recreateEsriSnapshotStatus.errors.length > 0) {
      toastError("Failed to create Esri snapshot. Please try again later.");
      console.error(recreateEsriSnapshotStatus.errors);
      recreateEsriSnapshotStatus.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recreateEsriSnapshotStatus.successful, recreateEsriSnapshotStatus.errors]);

  useEffect(() => {
    if (completePermitStatus.successful) {
      toastSuccess("Permit completed successfully!");
      setOpenCompleteModal(false);
      if (onPermitCompleted && permit) {
        onPermitCompleted(permit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completePermitStatus.successful]);

  const handleConfirmRejectModalSubmit = (comments: string) => {
    onRejectPermit(comments);
  };

  const handleConfirmCancelModalSubmit = () => {
    onCancelPermit();
  };

  const handleConfirmUndoCancellationModalSubmit = () => {
    onUndoCancelledPermit();
  };

  const handleExtendModalSubmit = (to: Date, comments: string) => {
    onExtendPermit(to, comments);
  };

  const handleCompleteModalSubmit = () => {
    onCompletePermit();
  };

  useEffect(() => {
    if (permit && permit.project.boundaries) {
      setProjectBoundaries([createProjectMapItem(permit.project)]);
    }
  }, [permit]);

  const PermitViewModal = (permit: Permit) => {
    return (
      <Modal open={openViewPermitModal} onClose={handleModalClose} size="fullscreen">
        <Modal.Content>
          <Modal.Description>{permitViewPanel(permit, true)}</Modal.Description>
        </Modal.Content>
      </Modal>
    );
  };

  const permitViewPanel = (permit: Permit, isPopup: boolean) => {
    return (
      <>
        {permit && (
          <>
            <Segment attached="top" basic secondary compact>
              <ActionButton
                icon="close"
                hint="Close permit view"
                onClick={() => {
                  isPopup ? handleModalClose() : onClose();
                }}
              />
              {!isPopup && <ActionButton icon="expand" hint="Expand permit view" onClick={handleViewPermitPopup} />}
              {organization && downloadPdfButtonVisible && (
                <PDFDownloadLink
                  document={<PermitPdf permit={permit} organization={organization} />}
                  fileName={`permit-${permit?.number}.pdf`}
                >
                  {({ blob, url, loading, error }) => (
                    <ActionButton
                      icon={loading ? "refresh" : "download"}
                      hint={loading ? "Loading" : "Download permit PDF"}
                    />
                  )}
                </PDFDownloadLink>
              )}
              {downloadPdfButtonVisible && (
                <ActionButton
                  icon={showPdf ? "list" : "file pdf"}
                  hint={showPdf ? "Show permit details" : "Show permit PDF"}
                  onClick={() => {
                    setShowPdf(!showPdf);
                  }}
                />
              )}
              {rejectButtonVisible && (
                <ActionButton onClick={(e) => setOpenConfirmRejectModal(true)} text="Reject" hint="Reject permit" />
              )}
              {approveButtonVisible && (
                <ActionButton
                  onClick={onApprovePermit}
                  text="Approve"
                  hint="Approve permit"
                  loading={approvePermitStatus.loading}
                  disabled={approvePermitStatus.loading}
                />
              )}
              {renewButtonVisible && <ActionButton text="Renew" hint="Renew permit" onClick={onRenewPermit} />}
              {extendButtonVisible && (
                <ActionButton text="Extend" hint="Extend permit" onClick={(e) => setOpenExtendModal(true)} />
              )}
              {cancelButtonVisible && (
                <ActionButton text="Cancel" hint="Cancel permit" onClick={(e) => setOpenConfirmCancelModal(true)} />
              )}
              {undoCancellationButtonVisible && (
                <ActionButton
                  text="Undo cancellation"
                  hint="Undo permit cancellation"
                  onClick={(e) => setOpenConfirmUndoCancellationModal(true)}
                ></ActionButton>
              )}
              {completeButtonVisible && (
                <ActionButton text="Complete" hint="Mark as complete" onClick={(e) => setOpenCompleteModal(true)} />
              )}
              {editButtonVisible && <ActionButton text="Edit" hint="Edit permit" onClick={onEditPermit} />}
              {reCreateEsriSnapshotVisible && (
                <ActionButton
                  text={recreateEsriSnapshotStatus.loading ? "Creating..." : "No ArcGIS PDF"}
                  disabled={recreateEsriSnapshotStatus.loading}
                  hint="Generate Now"
                  color="orange"
                  onClick={onRecreateEsriSnapshot}
                />
              )}
              <Header as="h3" style={{ display: "inline" }}>
                No. {permit.number.toString()}
                <Label size="tiny" circular>
                  {permit.type.name}
                </Label>
              </Header>
            </Segment>

            {organization && downloadPdfButtonVisible && showPdf && (
              <PDFViewer height={"650px"} width={"100%"}>
                <PermitPdf permit={permit} organization={organization} />
              </PDFViewer>
            )}

            {!(downloadPdfButtonVisible && showPdf) && (
              <>
                <Segment attached color="green" style={{ borderTop: "3px !important" }}>
                  <Grid>
                    <Grid.Column width={isPopup ? "6" : "16"}>
                      <PermitDescriptionView
                        permit={permit}
                        showHeader={isPopup}
                        organizationLogoUrl={organization?.logoUrl}
                        showAffectedServices={showAffectedServices}
                        showAsBuiltRequired={showAsBuiltRequired}
                      />
                    </Grid.Column>
                    {isPopup && (
                      <Grid.Column width="3" style={{ backgroundColor: "#f8f8f8" }}>
                        <Header as="h3">
                          <Icon name="users" />
                          <Header.Content> Permit team</Header.Content>
                        </Header>
                        <Divider />
                        {permit.permitAssignments.map((assignment) => (
                          <ViewItem
                            key={assignment.id}
                            header={assignment.orgRole.displayName}
                            value={assignment.orgUser?.user.displayName}
                          />
                        ))}

                        {showCalibrationCertificate && (
                          <PermitCalibrationCertificate
                            calibrationCertificate={permit.calibrationCertificate}
                          ></PermitCalibrationCertificate>
                        )}

                        <Card
                          content
                          style={{ "box-shadow": "0px 0px", backgroundColor: "transparent" }}
                          onClick={() => {
                            setPreviewDoc(undefined);
                            setShowAuthorization(true);
                            setPreviewSitePhoto(undefined);
                            setPreviewBoundaries(false);
                            setShowGangMembers(false);
                          }}
                        >
                          <Card.Content style={{ padding: "0px", margin: "0px" }}>View authorizations</Card.Content>
                        </Card>
                        {gangMembersEnabled && (
                          <Card
                            content
                            style={{ "box-shadow": "0px 0px", backgroundColor: "transparent" }}
                            onClick={() => {
                              setPreviewDoc(undefined);
                              setShowAuthorization(false);
                              setPreviewSitePhoto(undefined);
                              setPreviewBoundaries(false);
                              setShowGangMembers(true);
                            }}
                          >
                            <Card.Content style={{ padding: "0px", margin: "0px" }}>
                              Gang members ({permit.gangMembers.length})
                            </Card.Content>
                          </Card>
                        )}

                        {showPermitBoundariesOnMap && (
                          <PermitMap
                            permitHasBoundaries={true}
                            onPermitBoundariesClick={() => {
                              setPreviewDoc(undefined);
                              setShowAuthorization(false);
                              setPreviewSitePhoto(undefined);
                              setShowGangMembers(false);
                              setPreviewBoundaries(true);
                            }}
                          />
                        )}

                        <PermitAttachmentBar
                          documents={permit.documents}
                          selectedDocument={previewDoc}
                          onDocClicked={(doc) => {
                            setPreviewDoc(doc);
                            setShowAuthorization(false);
                            setPreviewSitePhoto(undefined);
                            setPreviewBoundaries(false);
                            setShowGangMembers(false);
                          }}
                          addNewDocumentButtonVisible={addNewDocumentButtonVisible}
                        />

                        {showSitePhotos && (
                          <PermitSitePhotos
                            sitePhotos={permit.sitePhotos}
                            onSitePhotoClicked={(photo) => {
                              setShowAuthorization(false);
                              setPreviewDoc(undefined);
                              setPreviewSitePhoto(photo);
                              setPreviewBoundaries(false);
                              setShowGangMembers(false);
                            }}
                            addNewSitePhotoButtonVisible={false}
                          ></PermitSitePhotos>
                        )}
                      </Grid.Column>
                    )}
                    {isPopup && (
                      <Grid.Column width="7" style={{ padding: "0px" }}>
                        {previewDoc && (
                          <DocumentView containerId="permitViewDocView" document={previewDoc} isPopup={false} />
                        )}
                        {permit && showAuthorization && <PermitAuthorizationView permit={permit} />}
                        {permit && previewSitePhoto && (
                          <PhotoViewer
                            comments={previewSitePhoto.comments}
                            dateCreated={previewSitePhoto.dateCreated}
                            createdBy={previewSitePhoto.createdBy as OrgUser}
                            url={previewSitePhoto.fileUrl}
                            type={previewSitePhoto.type}
                            affectedServiceName={previewSitePhoto.affectedServiceName}
                            noPhotoReason={previewSitePhoto.noPhotoReason}
                          />
                        )}
                        {permit && showGangMembers && <GangMembersView permit={permit} />}
                        {showPermitBoundariesOnMap && permit && previewBoundaries && (
                          <MapWithBoundaries
                            items={projectBoundaries}
                            boundaries={permit.areas}
                            withPopup={true}
                            popUpHasAction={false}
                          />
                        )}
                      </Grid.Column>
                    )}
                  </Grid>
                </Segment>
                <div>
                  <Segment floated="left" style={{ margin: "0px" }} basic>
                    <StateChangeLogsView logs={permit.stateChangeLogs} />
                  </Segment>
                  <Segment floated="right" style={{ margin: "0px" }} basic>
                    <LocationLogsView logs={permit.locationLogs} />
                  </Segment>
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      {permit && permitViewPanel(permit, false)}
      {permit && openViewPermitModal && PermitViewModal(permit)}
      <ConfirmWithCommentsModal
        title="Reject permit"
        open={openConfirmRejectModal}
        onClose={handleModalClose}
        onSubmit={handleConfirmRejectModalSubmit}
        submitInProgress={rejectPermitStatus.loading}
      ></ConfirmWithCommentsModal>

      <ConfirmModal
        title="Cancel permit"
        open={openConfirmCancelModal}
        onClose={handleModalClose}
        onSubmit={handleConfirmCancelModalSubmit}
      ></ConfirmModal>

      <ConfirmModal
        title="Undo Cancellation"
        open={openConfirmUndoCancellationModal}
        onClose={handleModalClose}
        onSubmit={handleConfirmUndoCancellationModalSubmit}
      ></ConfirmModal>

      <ExtendPermitModal
        title="Extend permit"
        open={openExtendModal}
        currentTo={permit?.validityDuration?.to}
        maxPermitDurationDays={permit?.template?.configuration?.maxPermitDurationDays}
        onClose={handleModalClose}
        onSubmit={handleExtendModalSubmit}
        submitInProgress={extendPermitStatus.loading}
      ></ExtendPermitModal>

      <CompletePermitModalContainer
        showAsBuilt={permit?.template?.features?.asBuilt?.enabled && permit?.asBuiltRequired}
        documents={
          permit?.asBuiltRequired
            ? permit?.documents.filter((d) => d.type.key === SystemDocumentType.SMD.key)
            : undefined
        }
        open={openCompleteModal}
        onClose={handleModalClose}
        onSubmit={handleCompleteModalSubmit}
        submitInProgress={completePermitStatus.loading}
        onAsBuiltAdded={onAsBuiltAdded}
        showGisDeclaration={showPermitBoundariesOnMap}
        asBuiltCompletionDeclarations={asBuiltCompletionDeclarations}
      ></CompletePermitModalContainer>
    </>
  );
};

export default PermitView;
